import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssessment } from '../context/AssessmentContext';
import ScoreOverview from '../components/results/ScoreOverview';
import SectionScores from '../components/results/SectionScores';
import ActionPlan from '../components/results/ActionPlan';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { calculateResults } from '../utils/scoring';

const { FiDownload, FiExternalLink, FiRefreshCw, FiArrowLeft, FiShield } = FiIcons;

export default function Results() {
  const { id } = useParams();
  const { businessInfo: contextInfo, answers: contextAnswers, resetAssessment } = useAssessment();
  const navigate = useNavigate();
  const [displayInfo, setDisplayInfo] = useState(null);
  const [displayResults, setDisplayResults] = useState(null);
  const [isHistorical, setIsHistorical] = useState(false);

  useEffect(() => {
    if (id) {
      const submissions = JSON.parse(localStorage.getItem('cb_submissions') || '[]');
      const sub = submissions.find(s => String(s.id) === String(id));
      
      if (sub) {
        setDisplayInfo(sub.businessInfo);
        if (sub.answers) {
          const cleanAnswers = {};
          Object.keys(sub.answers).forEach(k => {
            cleanAnswers[k] = typeof sub.answers[k] === 'object' ? sub.answers[k].value : sub.answers[k];
          });
          setDisplayResults(calculateResults(cleanAnswers));
        } else {
          setDisplayResults(sub.results);
        }
        setIsHistorical(true);
      } else {
        navigate('/admin');
      }
    } 
    else if (contextInfo && Object.keys(contextAnswers).length > 0) {
      const submissions = JSON.parse(localStorage.getItem('cb_submissions') || '[]');
      if (submissions.length > 0) {
        setDisplayInfo(contextInfo);
        setDisplayResults(submissions[0].results);
        setIsHistorical(false);
      }
    } else {
      navigate('/');
    }
    window.scrollTo(0, 0);
  }, [id, contextInfo, contextAnswers, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleRestart = () => {
    resetAssessment();
    navigate('/');
  };

  if (!displayResults || !displayInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 font-bold tracking-widest uppercase text-xs">Cargando Reporte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        {/* PDF Header Branding */}
        <div className="hidden print-only mb-10 border-b-4 border-slate-900 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiShield} className="text-4xl text-blue-600" />
              <div>
                <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Reporte de Seguridad</h1>
                <p className="text-blue-600 font-bold tracking-[0.1em] text-xs">
                  ProtegeTuRed by ComplianceBOX Evaluación de Seguridad
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-slate-900">{displayInfo.companyName}</p>
              <p className="text-xs text-slate-500">{new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        </div>

        {/* Screen Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-10 no-print gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(isHistorical ? '/admin' : '/')} 
              className="p-3 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-all shadow-sm group"
            >
              <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
            </button>
            <div>
              <h1 className="text-3xl font-black text-slate-900">
                {isHistorical ? 'Reporte Histórico' : 'Análisis de Seguridad'}
              </h1>
              <p className="text-slate-500 text-sm font-medium">ProtegeTuRed by ComplianceBOX</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handlePrint} 
              className="px-6 py-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-600/20 font-bold hover:bg-blue-700 flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
            >
              <SafeIcon icon={FiDownload} /> Descargar PDF
            </button>
            {!isHistorical && (
              <button 
                onClick={handleRestart} 
                className="p-3 bg-white border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
              >
                <SafeIcon icon={FiRefreshCw} />
              </button>
            )}
          </div>
        </div>

        <ScoreOverview results={displayResults} businessInfo={displayInfo} />
        <SectionScores sectionScores={displayResults.sectionScores} />
        <ActionPlan sectionScores={displayResults.sectionScores} />

        {/* Branded CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden print-break-inside-avoid">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -mr-32 -mt-32"></div>
          <div className="relative z-10">
            <h2 className="text-4xl font-black mb-6">Convierte este diagnóstico en acción</h2>
            <p className="text-blue-100 text-xl max-w-3xl mx-auto mb-10 leading-relaxed font-light">
              <strong>ComplianceBOX</strong> te ayuda a organizar políticas, tareas y revisiones internas para mantener tu seguridad actualizada.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 no-print">
              <a 
                href="https://compliancebox.org" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="px-10 py-5 bg-white text-blue-900 font-black rounded-2xl shadow-xl hover:bg-blue-50 transition-all flex items-center justify-center gap-2 transform hover:scale-105"
              >
                Visitar ComplianceBOX.org <SafeIcon icon={FiExternalLink} />
              </a>
            </div>
            <div className="hidden print-only py-4 border-2 border-white/20 rounded-2xl inline-block px-12 mt-4">
              <span className="text-2xl font-black tracking-widest uppercase">ProtegeTuRed by ComplianceBOX</span>
            </div>
          </div>
        </div>

        {/* Print Only Footer (for page numbering) */}
        <div className="print-footer hidden print-only"></div>

        {/* Disclaimer Footer */}
        <div className="mt-12 p-8 bg-slate-100 rounded-2xl text-center border border-slate-200 no-print">
          <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-4xl mx-auto">
            <strong>AVISO LEGAL:</strong> Este diagnóstico tiene fines exclusivamente educativos e informativos. No constituye una auditoría oficial ni asesoría legal. ComplianceBOX no asume responsabilidad por las decisiones tomadas basándose en este reporte.
          </p>
          <div className="mt-6 flex justify-center items-center gap-8 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
             <span>ProtegeTuRed</span>
             <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
             <span>by ComplianceBOX Evaluación de Seguridad</span>
          </div>
        </div>
      </div>
    </div>
  );
}